'use client';

import { ReactNode, useState } from 'react';

import { css, cx } from 'styled-system/css';

type Props = {
  /** mp4 played in place (wins over `embedSrc`, as in the original). */
  videoSrc?: string | null;
  /** YouTube-style embed URL, swapped in as an autoplaying iframe. */
  embedSrc?: string | null;
  /** Dim the play overlay (v2: `blackAlpha.400` when there is a poster / always for embeds). */
  dimOverlay?: boolean;
  /** Server-rendered poster/image, replaced by the player once activated. */
  children?: ReactNode;
};

// utils/helper-functions/sliderWithVideoHtml's `embedUrlWithAutoplay`, copied: that module imports Chakra.
const embedUrlWithAutoplay = (src: string): string => {
  try {
    const u = new URL(src);
    u.searchParams.set('autoplay', '1');
    return u.toString();
  } catch {
    return src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`;
  }
};

const fillClass = css({ position: 'absolute', top: 0, left: 0, w: 'full', h: 'full', border: 'none' });
const playButtonClass = css({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  _hover: { bg: 'blackAlpha.500' },
  '& svg': { w: { base: '72px', md: '100px' }, h: { base: '72px', md: '100px' } },
});
const dimClass = css({ bg: 'blackAlpha.400' });
const clearClass = css({ bg: 'transparent' });

/**
 * DynamicSliderWithVideoItem's `videoActive` state as an island: the poster/image is a server slot;
 * the play button swaps in the mp4 or the autoplaying embed iframe (nothing is fetched before the click).
 */
export const SlideMedia = ({ videoSrc, embedSrc, dimOverlay, children }: Props) => {
  const [active, setActive] = useState(false);
  const playIcon = (
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden>
      <rect width="100" height="100" rx="50" fill="#D9FC59" fillOpacity="0.6" />
      <path
        d="M71.2062 47.2558L41.94 29.3523C41.4466 29.0502 40.8814 28.8852 40.303 28.8745C39.7245 28.8638 39.1536 29.0077 38.6494 29.2914C38.1499 29.5706 37.7338 29.9779 37.444 30.4713C37.1541 30.9647 37.0008 31.5263 37 32.0986V67.9014C37.0038 68.7598 37.3482 69.5815 37.9577 70.186C38.5671 70.7905 39.3916 71.1282 40.25 71.125C40.8491 71.1247 41.4366 70.9596 41.9481 70.6476L71.2062 52.7442C71.6765 52.4575 72.0652 52.0548 72.3349 51.5745C72.6046 51.0943 72.7463 50.5528 72.7463 50.002C72.7463 49.4512 72.6046 48.9097 72.3349 48.4295C72.0652 47.9493 71.6765 47.5465 71.2062 47.2598V47.2558ZM40.25 67.8628V32.125L69.4655 50L40.25 67.8628Z"
        fill="#06030F"
      />
    </svg>
  );

  if (active && videoSrc) {
    return (
      <video
        className={cx(fillClass, css({ objectFit: 'contain' }))}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    );
  }
  if (active && embedSrc) {
    return (
      <iframe
        className={fillClass}
        src={embedUrlWithAutoplay(embedSrc)}
        title="Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <>
      {children}
      {(videoSrc || embedSrc) && (
        <button
          type="button"
          aria-label="Odtwórz wideo"
          className={cx(playButtonClass, dimOverlay ? dimClass : clearClass)}
          // As in the original: no preventDefault — on a slide whose whole card is a link the click
          // also follows the link.
          onClick={() => setActive(true)}
        >
          {playIcon}
        </button>
      )}
    </>
  );
};
